import { useMemo } from 'react';

import { articleView } from '@lumapps/articles/routes';
import { getArticleLink } from '@lumapps/articles/utils/getArticleLink';
import { CommunityLink } from '@lumapps/communities/components/CommunityLink';
import { RenderingType } from '@lumapps/communities/types';
import { ContentLink } from '@lumapps/contents/components/ContentLink';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { NavigationItemProps } from '@lumapps/lumx-navigation/components/NavigationItem';
import {
    isLinkNode,
    isPageNode,
    isSystemPageNode,
    NavigationItem,
    NavigationItemType,
    NavigationMapItem,
} from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { createUrl, isSameInstanceUrl } from '@lumapps/router/utils';
import { useSpace } from '@lumapps/spaces/hooks/useSpace';
import { useTranslate } from '@lumapps/translations';
import { getProtocolRelativeUrl } from '@lumapps/utils/string/getProtocolRelativeUrl';

import { SystemPagesLink } from '../components/SystemPagesLink';

const isNavigationMapItem = (node: NavigationItem | NavigationMapItem): node is NavigationMapItem =>
    Boolean((node as any).pageReference);

const getPageReference = (node: NavigationItem | NavigationMapItem) => {
    if (isNavigationMapItem(node)) {
        return { pageType: node.pageReference?.type, pageId: node.pageReference?.id };
    }
    return { pageType: node.pageType, pageId: node.pageId?.toString() || '' };
};

/**
 * Generate URL for navigation node.
 */
export const useNodeURL = (
    node: NavigationItem | NavigationMapItem,
): Partial<Pick<NavigationItemProps, 'linkProps' | 'linkAs'>> => {
    const { translateObject } = useTranslate();
    const currentInstance = useSelector(getCurrentInstance) || {};
    const { id: spaceId, slug: spaceSlug, isSpace } = useSpace();

    return useMemo(() => {
        const { pageType, pageId } = getPageReference(node);
        const translatedSlug = node?.slug?.value || translateObject(node?.slug);
        const slug = translatedSlug?.split('/').pop();
        const instance = { id: currentInstance.id, slug: currentInstance.slug };

        /**
         * If node is a link
         * or if the Content as a redirection link
         * */
        if (isLinkNode(node) || node.url) {
            const href = getProtocolRelativeUrl(
                typeof node.url === 'string' ? node.url : (translateObject(node.url) as string),
            );
            const isBlank = href && !isSameInstanceUrl(href);

            return { linkProps: { href, target: isBlank ? '_blank' : '_self', rel: 'noopener noreferrer' } };
        }

        if (isPageNode(node) && pageType === NavigationItemType.COMMUNITY && slug) {
            return {
                linkProps: { to: { slug, instance, id: pageId } },
                linkAs: CommunityLink,
            };
        }

        if (isPageNode(node) && pageType === NavigationItemType.CONTENT && slug && pageId) {
            return {
                linkProps: { to: { slug, type: pageType, id: pageId, instance } },
                linkAs: ContentLink,
            };
        }

        if (isPageNode(node) && pageType === NavigationItemType.ARTICLE && pageId) {
            const href = createUrl(articleView(pageId));
            const isBlank = href && !isSameInstanceUrl(href);

            if (isSpace) {
                const { linkAs, to } = getArticleLink(pageId, {
                    slug: spaceSlug,
                    communityId: spaceId,
                    renderingType: RenderingType.space,
                });

                return { linkAs, linkProps: { to } };
            }

            return { linkProps: { href, target: isBlank ? '_blank' : '_self', rel: 'noopener noreferrer' } };
        }

        if (isSystemPageNode(node) && node.systemPageReference?.type) {
            return {
                linkProps: { systemPageReference: node.systemPageReference.type },
                linkAs: SystemPagesLink,
            };
        }

        return {};
    }, [currentInstance.id, currentInstance.slug, isSpace, node, spaceId, spaceSlug, translateObject]);
};
